import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { CtyButton, CtySpinner } from '@circuitry-ai/doc-ui';
import { 
  useGetFgaGroupUsersQuery, 
  useGetFgaAllUsersQuery,
  useGetFgaGroupsQuery,
  useAddFgaUserMutation, 
  useRemoveFgaUserMutation,
  useFgaInviteMutation,
} from '../../../../shared/data/src/features/settings/settingsApiSlice';
import { handleApiError, handleApiSuccess } from '../fga-utils';

const UserCard = ({ user, onAction, actionIcon, actionColor, isLoading }: { 
  user: any; 
  onAction: () => void; 
  actionIcon: string; 
  actionColor: string;
  isLoading?: boolean;
}) => (
  <div className="bg-content1 border border-default-200 rounded-lg p-3 flex items-center justify-between">
    <div className="flex-1">
      <p className="font-medium text-foreground">{user.name}</p>
      <p className="text-sm text-default-500">{user.email}</p>
    </div>
    <button 
      onClick={onAction} 
      disabled={isLoading}
      className="p-2 hover:bg-default-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label={`${actionIcon === 'mdi:close' ? 'Remove' : 'Add'} user ${user.name}`}
    >
      {isLoading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-default-500"></div>
      ) : (
        <Icon icon={actionIcon} className={`w-6 h-6 ${actionColor}`} />
      )}
    </button>
  </div>
);

export interface UserManagementModalProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (isOpen: boolean) => void;
  readonly context?: 'group';
  readonly groupName?: string;
}

export function UserManagementModal({ 
  isOpen, 
  onOpenChange, 
  context = 'group',
  groupName
}: UserManagementModalProps) {
  const [activeTab, setActiveTab] = useState<'current' | 'add'>('current');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ firstName: '', lastName: '', email: '' });
  const [loadingStates, setLoadingStates] = useState({
    removingUsers: new Set<string>(),
    addingUsers: new Set<string>(),
    creatingUser: false
  });

  // API hooks
  const { data: allUsersData, isFetching: allUsersFetching, refetch: refetchAllUsers } = useGetFgaAllUsersQuery();
  const { refetch: refetchFgaGroups } = useGetFgaGroupsQuery();
  const [addFgaUser] = useAddFgaUserMutation();
  const [removeFgaUser] = useRemoveFgaUserMutation();
  const [fgaInvite] = useFgaInviteMutation();

  const allUsers = allUsersData?.data?.users || [];

  const { data: groupUsersData, isFetching: groupUsersFetching, refetch: refetchGroupUsers } = useGetFgaGroupUsersQuery(
    groupName || '', 
    { skip: !isOpen || !groupName }
  );

  const currentUsers = useMemo(
    () => (groupName ? groupUsersData?.data?.members || [] : []),
    [groupName, groupUsersData]
  );

  // Helper function to filter users by search term
  const filterUsersBySearch = useCallback((users: any[], term: string) => {
    if (!term.trim()) return users;
    const lowerTerm = term.toLowerCase();
    return users.filter((user: any) => 
      user.name.toLowerCase().includes(lowerTerm) || user.email.toLowerCase().includes(lowerTerm)
    );
  }, []);

  const availableUsers = useMemo(() => {
    const currentUserIds = new Set(currentUsers.map((user: any) => user.user_id));
    return allUsers.filter((user: any) => !currentUserIds.has(user.user_id));
  }, [allUsers, currentUsers]);

  const filteredUsers = useMemo(() => filterUsersBySearch(availableUsers, searchTerm), [availableUsers, searchTerm, filterUsersBySearch]);
  const filteredCurrentUsers = useMemo(() => filterUsersBySearch(currentUsers, searchTerm), [currentUsers, searchTerm, filterUsersBySearch]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setShowCreateForm(false);
      setNewUser({ firstName: '', lastName: '', email: '' });
    } else {
      refetchAllUsers();
      if (groupName) {
        refetchGroupUsers();
      }
    }
  }, [isOpen, groupName, refetchAllUsers, refetchGroupUsers]);

  const handleSingleUserAction = async (
    userId: string,
    targetGroup: string,
    apiCall: typeof addFgaUser | typeof removeFgaUser,
    loadingKey: 'addingUsers' | 'removingUsers',
    successMessage: string,
    action: 'add' | 'remove'
  ) => {
    setLoadingStates(prev => ({ ...prev, [loadingKey]: new Set([...prev[loadingKey], userId]) }));
    try {
      await apiCall({ user_id: userId, group: targetGroup }).unwrap();
      handleApiSuccess(successMessage);
      refetchGroupUsers();
      refetchFgaGroups();
    } catch (error) {
      handleApiError(error, `${action} user`);
    } finally {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: new Set([...prev[loadingKey]].filter(id => id !== userId)) }));
    }
  };

  const handleUserRemove = async (userId: string) => {
    const targetGroup = groupName || null;
    if (!targetGroup) {
      handleApiError(new Error('No target group available'), 'remove user');
      return;
    }
    await handleSingleUserAction(userId, targetGroup, removeFgaUser, 'removingUsers', 'User removed successfully', 'remove');
  };

  const handleUserAction = async (userId: string, action: 'add' | 'remove') => {
    if (action === 'add') {
      if (!groupName) {
        handleApiError(new Error('No target group available'), 'add user');
        return;
      }
      await handleSingleUserAction(userId, groupName, addFgaUser, 'addingUsers', 'User added successfully', action);
      return;
    }
    await handleUserRemove(userId);
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim()) return;
    if (!groupName) {
      handleApiError(new Error('No target group available'), 'create user');
      return;
    }
    setLoadingStates(prev => ({ ...prev, creatingUser: true }));
    try {
      const response = await fgaInvite({
        first_name: newUser.firstName.trim(),
        last_name: newUser.lastName.trim(),
        mail_id: newUser.email.trim(),
        groups: [groupName],
      }).unwrap();
      const message = (response as any)?.data?.message ?? (response as any)?.message ?? 'User created and added successfully';
      handleApiSuccess(message);
      refetchGroupUsers();
      refetchFgaGroups();
      setNewUser({ firstName: '', lastName: '', email: '' });
      setShowCreateForm(false);
    } catch (error) {
      handleApiError(error, 'create user');
    } finally {
      setLoadingStates(prev => ({ ...prev, creatingUser: false }));
    }
  };

  if (!isOpen) return null;

  const displayName = groupName ?? '';
  const description = groupName ? `Manage users assigned to the "${groupName}" group` : '';

  return (
    <>
      <button className="fixed inset-0 bg-black bg-opacity-50 z-[42]" onClick={() => onOpenChange(false)} />

      <div className="fixed top-0 right-0 h-full w-[450px] bg-content1 shadow-xl z-50 transform transition-transform duration-300 ease-out translate-x-0">
        <button onClick={() => onOpenChange(false)} className="absolute top-4 right-4 z-10 p-2 hover:bg-default-100 rounded-full">
          <Icon icon="mdi:close" className="w-5 h-5 text-default-500" />
        </button>

        <div className="p-6 border-b border-default-200">
          <h2 className="text-xl font-semibold text-foreground">User Management</h2>
          <p className="text-sm text-default-500 mt-1">{description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Icon icon="mdi:account-group" className="w-4 h-4 text-default-500" />
            <span className="text-sm text-default-500">{displayName}</span>
          </div>
        </div>

        <div className="px-6 pt-4">
          <div className="flex bg-default-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('current')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'current' ? 'bg-content1 dark:bg-[var(--cty-bg-hover)] text-foreground shadow-sm' : 'text-default-500 hover:text-foreground'
              }`}
            >
              <Icon icon="mdi:account-group" className="w-4 h-4" />
              Current Users ({groupUsersFetching ? '...' : currentUsers.length})
            </button>
            <button
              onClick={() => setActiveTab('add')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'add' ? 'bg-content1 dark:bg-[var(--cty-bg-hover)] text-foreground shadow-sm' : 'text-default-500 hover:text-foreground'
              }`}
            >
              <Icon icon="mdi:account-plus" className="w-4 h-4" />
              Add Users
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col" style={{ height: 'calc(100vh - 280px)' }}>
          {(allUsersFetching || groupUsersFetching) ? (
            <div className="flex-1 flex items-center justify-center">
              <CtySpinner size="md" />
            </div>
          ) : activeTab === 'current' ? (
            <>
              <div className="p-6 pb-4">
                <div className="relative">
                  <Icon icon="mdi:magnify" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-default-400" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-6 pb-4" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                {filteredCurrentUsers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <Icon icon="mdi:account-group-outline" className="w-16 h-16 text-default-300 mb-4" />
                    <p className="text-default-500 mb-2">No users assigned yet</p>
                    <p className="text-sm text-default-400">Add users using the 'Add Users' tab</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCurrentUsers.map((user: any) => (
                      <UserCard
                        key={user.user_id}
                        user={user}
                        onAction={() => handleUserAction(user.user_id, 'remove')}
                        actionIcon="mdi:close"
                        actionColor="text-default-500"
                        isLoading={loadingStates.removingUsers.has(user.user_id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="p-6 pb-4">
                <h3 className="text-lg font-medium text-foreground mb-2">Add User</h3>
                <p className="text-sm text-default-500 mb-4">
                  {groupName
                    ? `Search for existing users or create new ones to add to the "${groupName}" group.`
                    : 'Search for existing users or create new ones to add to the group.'}
                </p>

                <div className="relative">
                  <Icon icon="mdi:magnify" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-default-400" />
                  <input
                    type="text"
                    placeholder={`Search users to add to ${displayName}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-6 pb-4" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                {searchTerm.trim() ? (
                  <>
                    {filteredUsers.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-default-600 mb-3">Existing Users</p>
                        <div className="space-y-2">
                          {filteredUsers.map((user: any) => (
                            <UserCard
                              key={user.user_id}
                              user={user}
                              onAction={() => handleUserAction(user.user_id, 'add')}
                              actionIcon="mdi:plus"
                              actionColor="text-success"
                              isLoading={loadingStates.addingUsers.has(user.user_id)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {filteredUsers.length > 0 && (
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-default-200" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-content1 text-default-500">Or Create New</span>
                        </div>
                      </div>
                    )}
                    <button 
                      className="bg-content1 border border-default-200 rounded-lg p-4 cursor-pointer hover:bg-default-100 w-full text-left" 
                      onClick={() => setShowCreateForm(true)}
                      aria-label="Add new user to group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon icon="mdi:plus" className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Add new user</p>
                          <p className="text-sm text-default-500">Create a new user and assign to group</p>
                        </div>
                      </div>
                    </button>
                  </>
                ) : (
                  <>
                  <button
                      className="bg-content1 border border-default-200 rounded-lg p-4 cursor-pointer hover:bg-default-100 w-full text-left max-w-md"
                      onClick={() => setShowCreateForm(true)}
                      aria-label="Add new user to group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon icon="mdi:plus" className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Add new user</p>
                          <p className="text-sm text-default-500">Create a new user and assign to group</p>
                        </div>
                      </div>
                    </button>
                  {!showCreateForm && <div className="flex flex-col items-center justify-center h-full text-center">
                    <Icon icon="mdi:account-search-outline" className="w-16 h-16 text-default-300 mb-4" />
                    <p className="text-default-500 mb-2">Start typing to search for users</p>
                    <p className="text-sm text-default-400">or add a new user</p>
                  </div>}
                  </>
                )}
                {showCreateForm && (
                  <div className="mt-4 bg-default-100 rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-4">Create New User</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-medium text-default-600 mb-1">First name</label>
                          <input
                            id="firstName"
                            type="text"
                            value={newUser.firstName}
                            onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                            className="w-full px-3 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-sm font-medium text-default-600 mb-1">Last name</label>
                          <input
                            id="lastName"
                            type="text"
                            value={newUser.lastName}
                            onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                            className="w-full px-3 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-default-600 mb-1">Email address</label>
                        <input
                          id="email"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-3 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                      <div className="flex gap-3">
                        <CtyButton size="sm" variant="bordered" onPress={() => setShowCreateForm(false)}>Cancel</CtyButton>
                        <CtyButton 
                          size="sm" 
                          color="primary" 
                          onPress={handleCreateUser} 
                          isDisabled={!newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim() || loadingStates.creatingUser} 
                          isLoading={loadingStates.creatingUser}
                        >
                          Create User
                        </CtyButton>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 bg-content1 border-t border-default-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-default-500">
              {groupUsersFetching ? '...' : `${currentUsers.length} ${currentUsers.length === 1 ? 'user' : 'users'} in this group`}
            </span>
            <CtyButton color="primary" onPress={() => onOpenChange(false)}>Done</CtyButton>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserManagementModal;